import { useCallback, useRef } from "react";

// Client-side event tracking. Fires fire-and-forget POSTs to
// /api/rooms/{slug}/events; failures are swallowed (best-effort).
//
// Two passes of dedupe keep the event log signal-rich:
//   1. Per-event-key suppression (same {type+key} within DEDUPE_MS is dropped)
//   2. keepalive: true so events survive page-unload navigation
//
// Schema of the body sent to the API:
//   { type: string, payload?: Record<string, unknown> }
// The server stamps timestamp, session email (from JWT cookie), and the
// identity origin (magic | aad). Anonymous events are kept.

const DEDUPE_MS = 1500;

export interface TrackEventOptions {
    /**
     * Dedupe key. Same (type + key) within DEDUPE_MS is suppressed.
     * Default: stringified payload.
     */
    key?: string;
}

export function useTrackEvent(roomSlug: string | undefined) {
    const lastFired = useRef<Map<string, number>>(new Map());

    return useCallback(
        (type: string, payload?: Record<string, unknown>, options?: TrackEventOptions) => {
            if (!roomSlug) return;

            const dedupeKey = `${type}::${options?.key ?? JSON.stringify(payload ?? {})}`;
            const now = Date.now();
            const last = lastFired.current.get(dedupeKey);
            if (last !== undefined && now - last < DEDUPE_MS) return;
            lastFired.current.set(dedupeKey, now);

            const body = JSON.stringify({ type, payload: payload ?? {} });

            try {
                // Best-effort; do not await. Use keepalive so the request
                // survives if the user is navigating away.
                void fetch(`/api/rooms/${encodeURIComponent(roomSlug)}/events`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body,
                    keepalive: true,
                    credentials: "same-origin",
                });
            } catch {
                // Swallow — tracking is never allowed to throw into render.
            }
        },
        [roomSlug],
    );
}
